const BASE = '/api/partner';

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    credentials: 'include'
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Request failed: ${res.status}`);
  }
  return res.json();
}

export interface PortfolioStats {
  total_companies: number;
  active_companies: number;
  total_users: number;
  total_frameworks: number;
  avg_completion: number;
  companies_on_track: number;
}

export interface PortfolioCompany {
  id: number;
  name: string;
  legal_name: string | null;
  industry: string | null;
  size: string | null;
  is_active: boolean;
  created_at: string;
  framework_count: number;
  user_count: number;
  avg_completion: number;
}

export interface PortfolioFramework {
  id: number;
  name: string;
  code: string;
  company_count: number;
}

export interface ComplianceRow {
  company_id: number;
  company_name: string;
  framework_id: number;
  framework_name: string;
  framework_code: string;
  assignment_status: string;
  completion_percentage: number;
  assigned_at: string;
}

export interface Partner {
  id: number;
  name: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  website: string | null;
  is_active: boolean;
  company_count: number;
  user_count: number;
  created_at: string;
}

export const partnerApi = {
  // Partner admin — their own data
  getMe: () => request<{ partner: Partner }>(`${BASE}/me`),
  getStats: () => request<PortfolioStats>(`${BASE}/stats`),
  getCompanies: () => request<{ companies: PortfolioCompany[] }>(`${BASE}/companies`),
  getFrameworks: () => request<{ frameworks: PortfolioFramework[] }>(`${BASE}/frameworks`),
  getCompliance: (frameworkId?: number) =>
    request<{ data: ComplianceRow[] }>(`${BASE}/compliance${frameworkId ? `?framework_id=${frameworkId}` : ''}`),

  createCompany: (data: {
    name: string; legal_name?: string; registration_number?: string;
    industry?: string; size?: string; email?: string; phone?: string; website?: string;
  }) => request<{ company: { id: number; name: string } }>(`${BASE}/companies`, {
    method: 'POST', body: JSON.stringify(data)
  }),

  // System admin — manage all partners
  listPartners: () => request<{ partners: Partner[] }>(`${BASE}/admin/list`),
  createPartner: (data: {
    name: string; contact_name?: string; contact_email?: string;
    contact_phone?: string; website?: string; notes?: string;
  }) => request<{ partner: Partner }>(`${BASE}/admin/create`, {
    method: 'POST', body: JSON.stringify(data)
  }),
  updatePartner: (id: number, data: Partial<Partner>) =>
    request<{ success: boolean }>(`${BASE}/admin/${id}`, {
      method: 'PUT', body: JSON.stringify(data)
    }),
};

// Carbon credits API
export const carbonCreditsApi = {
  list: (companyId: number) =>
    request<{ credits: CarbonCredit[] }>(`/api/carbon-credits/${companyId}`),
  create: (companyId: number, data: Omit<CarbonCredit, 'id' | 'retirement_date' | 'status' | 'created_at' | 'updated_at' | 'company_id' | 'reminder_sent_60' | 'reminder_sent_30'>) =>
    request<{ credit: CarbonCredit }>(`/api/carbon-credits/${companyId}`, {
      method: 'POST', body: JSON.stringify(data)
    }),
  update: (companyId: number, creditId: number, data: Partial<CarbonCredit>) =>
    request<{ credit: CarbonCredit }>(`/api/carbon-credits/${companyId}/${creditId}`, {
      method: 'PUT', body: JSON.stringify(data)
    }),
  delete: (companyId: number, creditId: number) =>
    request<{ success: boolean }>(`/api/carbon-credits/${companyId}/${creditId}`, { method: 'DELETE' }),
};

export interface CarbonCredit {
  id: number;
  company_id: number;
  name: string;
  provider: string | null;
  credit_type: string;
  volume_tonnes: number;
  purchase_date: string;
  retirement_date: string;
  cost_per_tonne: number | null;
  total_cost: number | null;
  currency: string;
  certificate_url: string | null;
  serial_number: string | null;
  notes: string | null;
  status: 'active' | 'retired' | 'cancelled';
  reminder_sent_60: boolean;
  reminder_sent_30: boolean;
  created_at: string;
  updated_at: string;
}
